/**
 * Pages/Panel/Empleados/sbc.js
 *
 * Cálculo del Salario Base de Cotización (parte fija) y su estado de revisión.
 *
 * Es el gemelo de `erp_backend/Utils/prestaciones.utils.js`: **cualquier cambio va en los dos**.
 * El backend lo usa para pintar la columna del listado y para el cron de revisión; aquí lo usan
 * el botón F10 de Agregar/Editar Empleado y el Aumento de Sueldo. Antes cada una de esas tres
 * pantallas traía su propia copia de la fórmula, con convenciones distintas de antigüedad, así
 * que el botón y el listado podían dar números diferentes para el mismo empleado.
 *
 * `TipoPrestacion.filas[].valores`:
 *   [0] Antigüedad   [1] Días Vacaciones   [2] Porc. Prima Vacacional
 *   [3] Días Aguinaldo   [4] Factor Integración   [5] Días Antigüedad
 */

import moment from "moment";

/** Tolerancia por omisión al comparar el SBC capturado contra el calculado, en pesos. */
export const TOLERANCIA_SBC_DEFAULT = 0.5;

/** Base de cotización con salario VARIABLE puro: 2 Comisión, 3 Destajo. */
const BASES_COTIZACION_VARIABLES = [2, 3];

const redondear2 = (n) => Math.round(n * 100) / 100;

// El software de nómina externo escribe la fecha cero de Excel (30/12/1899) cuando el campo va
// vacío. Cualquier fecha anterior a 1900 es ese centinela disfrazado.
const fechaValida = (fecha) => {
  if (!fecha) return null;
  const m = moment(fecha);
  return m.isValid() && m.year() >= 1900 ? m : null;
};

/**
 * Fecha en que arranca la relación laboral vigente: el reingreso cuando existe y es POSTERIOR
 * al alta; si no, el alta. Es la misma regla de `Utils/empleado.utils.js` en el backend, y la
 * base de antigüedad del CFDI de nómina, el aguinaldo y el finiquito.
 */
export const inicioRelacionLaboral = (empleado) => {
  const alta = fechaValida(empleado?.fechaAlta);
  const reingreso = fechaValida(empleado?.fechaReingreso);
  if (reingreso && (!alta || reingreso.isAfter(alta))) return reingreso.toDate();
  return alta ? alta.toDate() : null;
};

/**
 * Día calendario reanclado a mediodía UTC.
 *
 * Contar años sobre los instantes en hora local se rompe con el horario de verano: México lo
 * abolió el 30/10/2022, así que una fecha de alta anclada a mediodía UTC cae a las 07:00 locales
 * antes de esa fecha y a las 06:00 después, y `diff('years')` devuelve un año de menos EL DÍA
 * DEL ANIVERSARIO. Además, así el navegador da el mismo número que el servidor sin importar la
 * zona horaria del usuario.
 */
const aDiaUtc = (fecha) => {
  const m = moment(fecha);
  if (!m.isValid()) return null;
  return moment.utc([m.year(), m.month(), m.date(), 12]);
};

/**
 * Antigüedad con la que se busca fila en la tabla.
 *
 * El `+1` NO es un error: la tabla "De Ley" arranca en la fila 1, no en la 0, así que un empleado
 * recién contratado tiene que caer en la fila 1. Quitarlo deja a todo el catálogo un escalón
 * abajo del factor que le toca.
 */
export const aniosParaTablaPrestacion = (inicio, fechaReferencia = new Date()) => {
  if (!inicio) return null;
  const desde = aDiaUtc(inicio);
  const hasta = aDiaUtc(fechaReferencia);
  if (!desde || !hasta) return null;
  if (hasta.isBefore(desde)) return null;
  return hasta.diff(desde, "years") + 1;
};

/** Fila aplicable: la de MAYOR antigüedad que no rebase la del empleado. */
export const filaPrestacion = (tipoPrestacion, anios) => {
  if (!tipoPrestacion?.filas?.length) return null;
  let fila = null;
  for (const f of tipoPrestacion.filas) {
    const v = Number(f.valores?.[0]);
    if (!isNaN(v) && v <= anios) {
      if (!fila || v > Number(fila.valores?.[0])) fila = f;
    }
  }
  return fila;
};

/** @returns {{factor: number|null, motivo: 'sinTabla'|'sinFilaAplicable'|'sinFactor'|null}} */
export const diagnosticarFactorIntegracion = ({ tipoPrestacion, anios }) => {
  if (!tipoPrestacion?.filas?.length) return { factor: null, motivo: "sinTabla" };
  const fila = filaPrestacion(tipoPrestacion, anios);
  if (!fila) return { factor: null, motivo: "sinFilaAplicable" };
  const factor = Number(fila.valores?.[4]);
  if (isNaN(factor) || factor <= 0) return { factor: null, motivo: "sinFactor" };
  return { factor, motivo: null };
};

/**
 * Datos de la fila aplicable del empleado — lo que consume Aumento de Sueldo para mostrar
 * vacaciones, prima y aguinaldo junto al factor.
 */
export const datosTablaPrestacion = ({ tipoPrestacion, empleado, fechaReferencia }) => {
  const anios = aniosParaTablaPrestacion(inicioRelacionLaboral(empleado), fechaReferencia);
  if (anios === null) return null;
  // null, no ceros: la pantalla pinta "—" cuando no hay fila, y un 0 se leería como un dato real.
  const fila = filaPrestacion(tipoPrestacion, anios);
  if (!fila) return null;
  return {
    anios,
    diasVacaciones: Number(fila.valores?.[1]) || 0,
    primaVacacional: Number(fila.valores?.[2]) || 0,
    diasAguinaldo: Number(fila.valores?.[3]) || 0,
    factorIntegracion: Number(fila.valores?.[4]) || 0,
  };
};

/** Texto de cada motivo de "no evaluable". */
export const MOTIVO_SBC_TEXTO = {
  salarioVariable: "Salario variable: la parte fija no se deriva del factor",
  sinFechaAlta: "Sin fecha de alta",
  altaFutura: "La fecha de alta es posterior a hoy",
  sinSalario: "Sin salario diario capturado",
  sinTabla: "Sin tipo de prestación asignado",
  sinFilaAplicable: "Su tabla de prestaciones no cubre esa antigüedad",
  sinFactor: "La fila de su tabla no tiene factor de integración",
};

/**
 * SBC parte fija que le corresponde al empleado. Devuelve también el porqué cuando no se puede
 * calcular, para que F10 pueda decirle al usuario qué le falta capturar.
 *
 * @param {object} params.empleado necesita `fechaAlta`, `fechaReingreso`, `salarioDiario` y
 *   `baseDeCotizacion`. El salario puede venir aparte en `salarioDiario` (el valor del formulario,
 *   que puede no estar guardado todavía).
 * @returns {{sbc: number|null, anios: number|null, factor: number|null, motivo: string|null}}
 */
export const calcularSbcParteFija = ({ empleado, tipoPrestacion, salarioDiario, fechaReferencia }) => {
  const vacio = (motivo, anios = null) => ({ sbc: null, anios, factor: null, motivo });

  if (BASES_COTIZACION_VARIABLES.includes(empleado?.baseDeCotizacion)) return vacio("salarioVariable");

  const inicio = inicioRelacionLaboral(empleado);
  if (!inicio) return vacio("sinFechaAlta");

  const anios = aniosParaTablaPrestacion(inicio, fechaReferencia);
  if (anios === null) return vacio("altaFutura");

  const salario = Number(salarioDiario != null ? salarioDiario : empleado?.salarioDiario);
  if (isNaN(salario) || salario <= 0) return vacio("sinSalario", anios);

  const { factor, motivo } = diagnosticarFactorIntegracion({ tipoPrestacion, anios });
  if (!factor) return vacio(motivo, anios);

  return { sbc: redondear2(salario * factor), anios, factor, motivo: null };
};

// ─── Estado de revisión (lo calcula el backend; aquí sólo se pinta) ──────────

export const SBC_LABEL = {
  ok: "Correcto",
  menor: "Menor al calculado",
  mayor: "Mayor al calculado",
  noEvaluable: "No evaluable",
};

/** Colores de `Tag` de antd, mismo criterio que EtiquetaEstado en ModalValidacionTimbrado. */
export const SBC_TAG_COLOR = {
  ok: "success",
  menor: "error",
  mayor: "warning",
  noEvaluable: "default",
};

/** Colores planos para la leyenda y el banner (paleta de App.css). */
export const SBC_COLOR = {
  ok: "#3B6D11",
  menor: "#CF1322",
  mayor: "#C9A227",
  noEvaluable: "#8C8C8C",
};

/** Clase de fila de la tabla. `ok` no pinta nada: el caso normal no debe gritar. */
export const claseFilaSbc = (estado) =>
  estado === "menor" ? "sbc-menor" : estado === "mayor" ? "sbc-mayor" : null;

export const nombreEstadoSbc = (estado) => SBC_LABEL[estado] || SBC_LABEL.noEvaluable;

/** Los dos estados que cuentan como "hay que revisarlo". */
export const esSbcDesfasado = (estado) => estado === "menor" || estado === "mayor";

/** Resumen para el banner y el modal. */
export const resumirRevisionSbc = (empleados = []) => {
  const resumen = { menor: 0, mayor: 0, noEvaluable: 0, ok: 0, desfasados: 0 };
  for (const e of empleados) {
    const estado = e?.sbcRevision?.estado;
    if (!estado || resumen[estado] === undefined) continue;
    resumen[estado] += 1;
    if (esSbcDesfasado(estado)) resumen.desfasados += 1;
  }
  return resumen;
};
